import { Modal } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import styles from './LogsPage.module.scss'
import { useAppSelector } from '../../hooks/useAppSelector'
import { IProduct } from '../../types/product'
import { toSentenceCase } from '../../utils/toSentenceCase'
import { useEffect, useMemo } from 'react'
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table'
import { fuzzyFilter } from '../../utils/fuzzyFilter'
import { ILiveLog, LEVELS } from '../../types/logs'
import { NoData } from '../NoData'
import { useAppDispatch } from '../../hooks/useAppDispatch'
import { selectOneProductLiveLogs } from '../../store/selectors/logsSelectors'
import { selectOneProduct } from '../../store/selectors/productsSelectors'
import { fetchLiveLogs } from '../../store/async-thunks/logs/fetchLiveLogs'
import { ProductTime } from '../ProductTime/ProductTime'
import { getSameDatetimeLocalFormatWithUTC } from '../../utils/getProductTime'

const emptyArr: any = []

export const LogsPage = () => {
    const dispatch = useAppDispatch()
    const { productId } = useParams<{ productId: string }>()
    const curProduct = useAppSelector(selectOneProduct(productId)) as IProduct
    const productLiveLogs = useAppSelector(selectOneProductLiveLogs(productId)) ?? emptyArr

    const columns = useMemo<ColumnDef<unknown>[]>(
        () => [
            {
                accessorKey: 'log_time',
                cell: (info) => {
                    if (info.getValue()) {
                        return getSameDatetimeLocalFormatWithUTC(info.getValue() as string)
                    }
                },
                header: () => 'PRODUCT TIME',
            },
            {
                accessorKey: 'level',
                cell: (info) => LEVELS[info.getValue() as keyof typeof LEVELS],
                header: () => 'LEVEL',
            },
            {
                accessorKey: 'tag',
                cell: (info) => info.getValue(),
                header: () => 'TAG',
            },
            {
                accessorKey: 'message_template',
                cell: (info) => info.getValue(),
                header: () => 'MESSAGE',
            },
        ],
        []
    )

    const table = useReactTable({
        data: productLiveLogs,
        columns: columns,
        filterFns: { fuzzy: fuzzyFilter },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        debugTable: true,
    })

    useEffect(() => {
        const abortController = new AbortController()
        dispatch(
            fetchLiveLogs({
                productGuid: productId,
                signal: abortController.signal,
            })
        )

        return () => abortController.abort()
    }, [dispatch, productId])

    return (
        <Modal
            show
            fullscreen
            keyboard={false}
            className="p-0 m-0"
        >
            <Modal.Body className={`app-bg-primary ${styles.container}`}>
                <h4>Live logs</h4>
                <div className="d-flex justify-content-between">
                    <span>
                        Product:{' '}
                        <span className={styles.pName}>
                            {curProduct ? toSentenceCase(curProduct.name) : ''}
                        </span>
                    </span>
                    <ProductTime guid={productId} />
                </div>
                {!!productLiveLogs.length ? (
                    <table className={styles.table}>
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <th
                                                key={header.id}
                                                colSpan={header.colSpan}
                                                className={styles.tableHead}
                                            >
                                                {header.isPlaceholder ? null : (
                                                    <div>
                                                        {flexRender(
                                                            header.column
                                                                .columnDef
                                                                .header,
                                                            header.getContext()
                                                        )}
                                                    </div>
                                                )}
                                            </th>
                                        )
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row) => {
                                const originalLiveLogObj =
                                    row.original as ILiveLog
                                const lvl = originalLiveLogObj.level
                                return (
                                    <tr
                                        key={row.id}
                                        className={`${styles.tableRow} ${styles[lvl]}`}
                                    >
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <td
                                                    key={cell.id}
                                                    className={styles.tableCell}
                                                >
                                                    <div
                                                        className={
                                                            styles.cellContent
                                                        }
                                                    >
                                                        {flexRender(
                                                            cell.column
                                                                .columnDef.cell,
                                                            cell.getContext()
                                                        )}
                                                    </div>
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                ) : (
                    <NoData style={{ height: '300px' }} />
                )}
            </Modal.Body>
        </Modal>
    )
}
